import {
  take,
  put,
  select,
  takeLatest,
  cancel,
  call,
} from 'redux-saga/effects';

import { SCHEDULE_VALIDATE_TYPES, scheduleValidateActions } from '../validate';
import {
  selectSchedulePopoverData,
  selectScheduleTargerProviderId,
} from '../selectors';
import {
  RECURSION_MODAL_TYPES,
  RECURSION_OPTIONS,
} from '../../../components/ProviderCalendar/constants';
import {
  getPrevDayRepeatableAdapter,
  getRepeatFlow,
  isFirstDayOfCustomRepeatChange,
  isLastDayOfRepeat,
  isOnlyEndRepeatDateChange,
  noRepeatScheduleAdapter,
  removeRepeatScheduleAdapter,
  scheduleCurrentDaySettingsAttrsAdapter,
  scheduleOriginDaySettingsAttrsAdapter,
} from '../../../utils/schedule/repeat-schedule-adapter';
import * as blockslotInsert from '../requests/blockslot-insert-req';
import * as blockslotRemove from '../requests/blockslot-remove-req';
import * as blockslotUpdate from '../requests/blockslot-update-req';

function* displayRecursionModal(recursionFlow) {
  const selectedSchedule = yield select(selectSchedulePopoverData);

  yield put(
    scheduleValidateActions.toggleRecursionModal({
      type: RECURSION_MODAL_TYPES.EDIT_BLOCK_SLOT,
      lastDay: isLastDayOfRepeat(selectedSchedule),
      opened: true,
      recursionFlow,
    }),
  );

  const {
    payload: { option: recursionOption },
  } = yield take(SCHEDULE_VALIDATE_TYPES.APPLY_RECURSION);

  return recursionOption;
}

function* proceedNonRepeatableChange({ selectedSchedule, repeatData, id }) {
  const attrs = scheduleOriginDaySettingsAttrsAdapter(selectedSchedule);
  const scheduleSettingAttributes = {
    ...attrs,
    ...repeatData,
    endOn: repeatData.endOn || null,
  };
  const updatePayload = {
    slotId: selectedSchedule.id,
    blockedSlot: { scheduleSettingAttributes },
    id,
  };
  yield put(blockslotUpdate.actions.send(updatePayload));
}

function* proceedCustomRepeatWithFollowingOption({
  selectedSchedule,
  repeatData,
  id,
}) {
  const attrs = scheduleCurrentDaySettingsAttrsAdapter(selectedSchedule);

  const updatePayload = {
    slotId: selectedSchedule.id,
    blockedSlot: { ...getPrevDayRepeatableAdapter(selectedSchedule) },
    id,
  };
  const insertPayload = {
    blockedSlot: {
      scheduleSettingAttributes: { ...attrs, ...repeatData },
    },
    id,
  };
  yield put(blockslotUpdate.actions.send(updatePayload));
  yield take(blockslotUpdate.actions.succeed);
  yield put(blockslotInsert.actions.send(insertPayload));
}

function* proceedCustomRepeatWithAllOption({
  selectedSchedule,
  repeatData,
  id,
}) {
  const attrs = scheduleOriginDaySettingsAttrsAdapter(selectedSchedule);
  const updatePayload = {
    slotId: selectedSchedule.id,
    blockedSlot: {
      scheduleSettingAttributes: { ...attrs, ...repeatData },
    },
    id,
  };
  yield put(blockslotUpdate.actions.send(updatePayload));
}

function* proceedNoRepeatWithFollowingOption({ selectedSchedule, id }) {
  const removedSlot = noRepeatScheduleAdapter(selectedSchedule);

  yield put(blockslotRemove.actions.send({ id, removedSlot }));
}

function* proceedNoRepeatWithAllOption({ selectedSchedule, id }) {
  const removedSlot = removeRepeatScheduleAdapter(
    RECURSION_OPTIONS.ALL_DAYS,
    selectedSchedule,
  );

  const scheduleSettingAttributes = removeRepeatScheduleAdapter(
    RECURSION_OPTIONS.CURRENT_DAY,
    selectedSchedule,
  );

  const blockedSlot = { ...scheduleSettingAttributes };
  yield put(blockslotRemove.actions.send({ id, removedSlot }));
  yield take(blockslotRemove.actions.succeed);
  yield put(blockslotInsert.actions.send({ id, blockedSlot }));
}

function* handleEditBlockRecursion({ payload }) {
  const id = yield select(selectScheduleTargerProviderId);
  const selectedSchedule = yield select(selectSchedulePopoverData);
  const { repeatData, repeatValue } = payload;
  const recursionFlow = getRepeatFlow(repeatValue);
  const shared = { id, selectedSchedule, repeatData };

  if (!selectedSchedule.isRepeatable) {
    yield call(proceedNonRepeatableChange, shared);
    yield cancel();
  }

  if (isOnlyEndRepeatDateChange(selectedSchedule, payload)) {
    yield call(proceedCustomRepeatWithAllOption, shared);
    yield cancel();
  }

  if (isFirstDayOfCustomRepeatChange(selectedSchedule, recursionFlow)) {
    yield call(proceedCustomRepeatWithAllOption, shared);
    yield cancel();
  }

  const recursionOption = yield call(displayRecursionModal, recursionFlow);

  switch (recursionOption) {
    case RECURSION_OPTIONS.UPDATE_CUSTOM_FOLOWING: {
      yield call(proceedCustomRepeatWithFollowingOption, shared);
      yield cancel();
      break;
    }

    case RECURSION_OPTIONS.UPDATE_CUSTOM_ALL: {
      yield call(proceedCustomRepeatWithAllOption, shared);
      yield cancel();
      break;
    }

    case RECURSION_OPTIONS.NO_REPEAT_FOLOWING: {
      yield call(proceedNoRepeatWithFollowingOption, shared);
      yield cancel();
      break;
    }

    case RECURSION_OPTIONS.NO_REPEAT_ALL: {
      yield call(proceedNoRepeatWithAllOption, shared);
      yield cancel();
      break;
    }

    default:
      console.log('unhandled recursion option', recursionOption);
      yield cancel();
  }
}

export default function* editBlockRecursionSaga() {
  yield takeLatest(
    [SCHEDULE_VALIDATE_TYPES.EDIT_BLOCK_RECURSION],
    handleEditBlockRecursion,
  );
}



// WEBPACK FOOTER //
// ./src/redux/schedule/sagas/edit-block-recursion-saga.js